import { useEffect, useRef } from 'react';
import { BACKGROUND_READ_TIMEOUT_MS } from '../utils/readTimeout';

// Last-resort bound on ONE run (B02). The real fix is that every read this
// hook drives now carries `BACKGROUND_READ_TIMEOUT_MS` on the request itself,
// so a hung socket rejects and the ordinary error path recovers the schedule.
// This deadline exists only for a callback that ignores that bound; the grace
// keeps it strictly later than the request's own timeout, so the normal
// recovery path stays the request's, not this one's.
const RUN_DEADLINE_GRACE_MS = 5000;

/**
 * Shared shell polling (PERF-API-01/P01).
 *
 * The app shell used to run three unconditional `setInterval`s — WhatsApp
 * unread, notifications, announcements — that kept firing while the tab sat in
 * the background. This hook keeps the same resources and the same authorized
 * requests; it only changes WHEN they are asked for:
 *
 *   - one run on enable (unchanged startup behaviour),
 *   - the interval ticks only while the document is visible,
 *   - hiding the tab cancels the timer instead of polling into the void,
 *   - returning to a visible tab (or coming back online) resyncs, but only
 *     when the data is already older than the interval — a quick alt-tab does
 *     not turn into an extra request,
 *   - one run in flight at a time per hook instance; a tick that lands while
 *     the previous run is still pending is dropped, not queued.
 *
 * That in-flight slot is bounded (B02). A read that never settles used to hold
 * it forever, so neither the timer, nor returning to the tab, nor coming back
 * online could ever refresh again. The reads themselves now carry a request
 * deadline (`BACKGROUND_READ_TIMEOUT_MS`); on top of that, a run still pending
 * past the deadline is CANCELLED — the returned handle's `.abort()` when it has
 * one — before the slot is released, so recovering freshness never means
 * stacking a second uncancelled copy of the same request on the first.
 *
 * `callback` may return a promise; when it does, the in-flight guard waits for
 * it. It is read through a ref, so a caller does not have to memoize it for
 * the timer to stay stable — but `intervalMs`/`enabled` changes do restart the
 * cycle, which is what a scope change (permission, feature, tenant) needs.
 */
export default function useVisiblePolling(
  callback,
  intervalMs,
  { enabled = true, runDeadlineMs = BACKGROUND_READ_TIMEOUT_MS + RUN_DEADLINE_GRACE_MS } = {}
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let disposed = false;
    let timer = null;
    let running = false;
    let lastRunAt = 0;
    // Monotonic id of the run that currently owns the in-flight slot. A run
    // whose deadline already expired is no longer the owner, so when its
    // answer finally turns up it cannot clear a flag that now belongs to a
    // newer read.
    let runSeq = 0;
    let currentHandle = null;

    const isVisible = () =>
      typeof document === 'undefined' || document.visibilityState !== 'hidden';

    // RTK's dispatch(thunk()) promise cancels the underlying request; a plain
    // promise (or a test double) has no `.abort`, and must not be called.
    const cancel = (handle) => {
      if (handle && typeof handle.abort === 'function') {
        try {
          handle.abort();
        } catch (e) {
          // Cancelling is best-effort; a throw here must not stop the schedule.
        }
      }
    };

    const run = async () => {
      // Coalesce: a slow response must not stack a second identical read on
      // top of itself when the tab wakes up and the timer fires together.
      if (disposed || running) return;
      runSeq += 1;
      const runId = runSeq;
      running = true;
      lastRunAt = Date.now();

      const deadline = setTimeout(() => {
        if (runId !== runSeq) return;
        // Cancel FIRST, then free the slot. Releasing without cancelling is
        // how one hung read becomes an ever-growing pile of live ones.
        cancel(currentHandle);
        currentHandle = null;
        running = false;
      }, runDeadlineMs);

      let handle;
      try {
        handle = callbackRef.current();
        currentHandle = handle;
        await handle;
      } catch (e) {
        // The callers already own their error UX (silent background misses for
        // the badge/announcements). Never let a rejected background read stop
        // the schedule.
      } finally {
        clearTimeout(deadline);
        if (runId === runSeq) {
          if (currentHandle === handle) currentHandle = null;
          running = false;
        }
      }
    };

    const stopTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startTimer = () => {
      stopTimer();
      timer = setInterval(() => {
        if (isVisible()) run();
      }, intervalMs);
    };

    const resync = () => {
      if (disposed || !isVisible()) return;
      // Bounded staleness: only refresh when a whole interval has already been
      // missed, so focus churn cannot amplify requests.
      if (Date.now() - lastRunAt >= intervalMs) run();
      startTimer();
    };

    const onVisibility = () => {
      if (isVisible()) resync();
      else stopTimer();
    };

    run();
    if (isVisible()) startTimer();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', resync);

    return () => {
      disposed = true;
      stopTimer();
      // Unmount, logout, a closed permission/feature gate: nobody is left to
      // read the answer, so the request goes with the listeners. Callers treat
      // `meta.aborted` as an abandoned read, never as a user-facing error.
      runSeq += 1;
      cancel(currentHandle);
      currentHandle = null;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', resync);
    };
  }, [intervalMs, enabled, runDeadlineMs]);
}
